import React from "react";
// BaseField not used in SectionRenderer; removed to satisfy lint

export default function SectionRenderer({
  field,
  value,
  setValue,
  allFields,
  branchDepth = 0,
  renderChild,
  renderInlineChildren,
  openViewer,
  previewMode = false,
}) {
  const required = !!field.required;
  const label = field.label || "Section";
  const repeatable = !!field.config?.repeatable;
  const childFields = Array.isArray(field.children) && field.children.length > 0
    ? field.children
    : Array.isArray(field.fields)
      ? field.fields
      : [];
  const isWorkflow = field.type === "split_workflow";

  // Determine instances to render based on the stored `value` (repeatable sections store an array)
  const instancesCount = Array.isArray(value) ? Math.max(1, value.length) : (field.instances ? field.instances.length : 1);

  const handleAddInstance = () => {
    const newValue = Array.isArray(value) ? [...value, {}] : [{}, ...(value ? [value] : [])];
    setValue(newValue);
  };

  const handleRemoveInstance = (idx) => {
    if (!Array.isArray(value)) return;
    const newValue = value.filter((_, i) => i !== idx);
    setValue(newValue);
  };

  return (
    <div className="mb-6">
      {/* Header + Add Another */}
      <div className={previewMode ? "mb-3 flex flex-col gap-2" : "flex items-center justify-between mb-3"}>
        <h4 className="text-blue-700 dark:text-blue-300 font-semibold flex items-center gap-2">
          📁 {label} {required && <span className="text-orange-500">*</span>}
        </h4>

        {repeatable && (
          <button
            type="button"
            onClick={handleAddInstance}
            className={`${previewMode ? "w-full text-xs px-3 py-2" : "text-sm px-3 py-1"} bg-blue-600 hover:bg-blue-700 text-white rounded-lg shadow-sm transition-colors`}
          >
            ➕ Add Another
          </button>
        )}
      </div>

      {/* Each instance gets its OWN separate box */}
      {Array.from({ length: instancesCount }).map((_, i) => (
        <div
          key={`${field.id || "section"}_${i}`}
          className={`border-2 border-blue-500 dark:border-blue-700 rounded-2xl ${previewMode ? "p-3 mb-4" : "p-4 mb-6"} bg-blue-50/60 dark:bg-blue-900/30 shadow-sm`}
        >
          {repeatable && instancesCount > 1 && (
            <div className={`mb-2 ${previewMode ? "flex flex-col gap-2" : "flex justify-between items-center"}`}>
              <span className="text-xs text-blue-500 dark:text-blue-400">
                Instance #{i + 1}
              </span>
              <button
                type="button"
                className="text-xs text-red-600 hover:text-red-700 dark:text-red-400 dark:hover:text-red-300"
                onClick={() => handleRemoveInstance(i)}
              >
                ✕ Remove
              </button>
            </div>
          )}

          {/* Render child fields */}
          {childFields.map((childField) => {
            const instanceVal = Array.isArray(value) ? (value[i] || {}) : (value || {});
            const childValue = instanceVal ? instanceVal[childField.id] : undefined;

            const setChildValue = (v) => {
              if (Array.isArray(value)) {
                const newValue = [...value];
                newValue[i] = { ...(newValue[i] || {}), [childField.id]: v };
                setValue(newValue);
              } else {
                setValue({ ...(value || {}), [childField.id]: v });
              }
            };

            const rendered = renderChild?.(childField, childValue, setChildValue);
            if (React.isValidElement(rendered)) {
              return React.cloneElement(rendered, { key: childField.id });
            }
            // If renderChild returned a non-element (string/node), wrap in fragment with key
            return <React.Fragment key={childField.id}>{rendered}</React.Fragment>;
          })}
        </div>
      ))}

      {renderInlineChildren?.()}
    </div>
  );
}
